import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { environment } from '../environments/environment';
import { finalize, catchError } from 'rxjs/operators';

/**
 * Servicio puente para todas las llamadas a la API
 * El token de autenticación es manejado automáticamente por AuthInterceptorService
 */
@Injectable({
  providedIn: 'root',
})
export class ApiBridgeService {
  private requestInProgressSource = new BehaviorSubject<boolean>(false);
  public requestInProgress$ = this.requestInProgressSource.asObservable();

  constructor(private httpClient: HttpClient) {}

  /**
   * Realiza una petición POST
   * @param endpoint - Endpoint relativo de la API
   * @param body - Datos a enviar
   * @param customHeaders - Headers adicionales opcionales
   */
  post<T = any>(
    endpoint: string,
    body: any,
    customHeaders?: Record<string, string>
  ): Observable<T> {
    this.requestInProgressSource.next(true);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    return this.httpClient
      .post<T>(`${environment.api_url}/${endpoint}`, body, { headers })
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }

  /**
   * Realiza una petición POST multipart (para archivos)
   * @param endpoint - Endpoint relativo de la API
   * @param formData - FormData con los archivos/datos
   * @param customHeaders - Headers adicionales opcionales
   */
  postMultipart<T = any>(
    endpoint: string,
    formData: FormData,
    customHeaders?: Record<string, string>
  ): Observable<T> {
    this.requestInProgressSource.next(true);

    const headers = new HttpHeaders({
      accept: 'application/json, text/plain, */*',
      ...customHeaders,
    });
    // No establecer Content-Type para multipart, el navegador lo hace automáticamente

    return this.httpClient
      .post<T>(`${environment.api_url}/${endpoint}`, formData, { headers })
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }

  /**
   * Realiza una petición GET
   * @param endpoint - Endpoint relativo de la API
   * @param queryParams - Parámetros de consulta opcionales
   */
  get<T = any>(
    endpoint: string,
    queryParams: Record<string, any> = {}
  ): Observable<T> {
    this.requestInProgressSource.next(true);

    let params = new HttpParams();
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== null && queryParams[key] !== undefined) {
        params = params.set(key, queryParams[key].toString());
      }
    });

    return this.httpClient
      .get<T>(`${environment.api_url}/${endpoint}`, { params })
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }

  /**
   * Realiza una petición PUT
   * @param endpoint - Endpoint relativo de la API
   * @param body - Datos a actualizar
   */
  put<T = any>(endpoint: string, body: any): Observable<T> {
    this.requestInProgressSource.next(true);

    return this.httpClient
      .put<T>(`${environment.api_url}/${endpoint}`, body)
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }

  /**
   * Realiza una petición PATCH
   * @param endpoint - Endpoint relativo de la API
   * @param body - Datos parciales a actualizar
   */
  patch<T = any>(endpoint: string, body: any): Observable<T> {
    this.requestInProgressSource.next(true);

    return this.httpClient
      .patch<T>(`${environment.api_url}/${endpoint}`, body)
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }

  /**
   * Realiza una petición DELETE
   * @param endpoint - Endpoint relativo de la API
   */
  delete<T = any>(endpoint: string): Observable<T> {
    this.requestInProgressSource.next(true);

    return this.httpClient
      .delete<T>(`${environment.api_url}/${endpoint}`)
      .pipe(
        catchError((err: any) => throwError(() => err)),
        finalize(() => this.requestInProgressSource.next(false))
      );
  }
}
